import { useCallback, useEffect, useRef } from 'react';
import { getAuth } from 'firebase/auth';

// Logs a member opening a suggested profile (backend/routes/suggestionViews.js):
// once when it opens, again when they answer, and when they close it — with
// how long it was on screen and how many photos they swiped through.
// Best-effort only: failures never affect the profile screen.

const newId = () => ((crypto.randomUUID && crypto.randomUUID()) || `pv_${Date.now()}_${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9_-]/g, '');

const useSuggestionProfileView = ({ viewedUserId, communityId, conversationId, photoIndex, photoCount }) => {
  const view = useRef(null);

  const send = useCallback((extra = {}) => {
    const v = view.current;
    if (!v || !v.token) return;
    const activeMs = v.accumulatedMs + (v.visibleSince ? Date.now() - v.visibleSince : 0);
    try {
      fetch(`${process.env.REACT_APP_API_BASE_URL}/suggestion-views`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${v.token}` },
        body: JSON.stringify({
          viewId: v.viewId,
          viewedUserId: v.viewedUserId,
          communityId: v.communityId,
          conversationId: v.conversationId,
          activeMs,
          photosSeen: v.maxPhoto + 1,
          photoCount: v.photoCount,
          ...extra,
        }),
      }).catch(() => {});
    } catch { /* ignore */ }
  }, []);

  // One "view" per profile opened.
  useEffect(() => {
    if (!viewedUserId) return undefined;
    const v = {
      viewId: newId(),
      viewedUserId: String(viewedUserId),
      communityId: communityId ? String(communityId) : null,
      conversationId: conversationId ? String(conversationId) : null,
      photoCount: photoCount || 0,
      maxPhoto: 0,
      accumulatedMs: 0,
      visibleSince: document.visibilityState === 'visible' ? Date.now() : null,
      token: null,
    };
    view.current = v;

    // The token is fetched up front so closing can send straight away.
    getAuth().currentUser?.getIdToken()
      .then((token) => { v.token = token; if (view.current === v) send(); })
      .catch(() => {});

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (v.visibleSince) v.accumulatedMs += Date.now() - v.visibleSince;
        v.visibleSince = null;
        send();
      } else if (!v.visibleSince) {
        v.visibleSince = Date.now();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      send({ closed: true });
      if (view.current === v) view.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewedUserId]);

  useEffect(() => {
    if (view.current && photoIndex > view.current.maxPhoto) view.current.maxPhoto = photoIndex;
  }, [photoIndex]);

  // 'interested' / 'not_interested'
  const recordOutcome = useCallback((outcome) => send({ outcome }), [send]);

  return { recordOutcome };
};

export default useSuggestionProfileView;
